import { Fragment, useState, useCallback } from 'react';
import clsx from 'clsx';
import type { IOption } from '@/types/features/questionnaire/question';
import ExpandableTextarea from './ExpandableTextarea';
import HelperDialog from './HelperDialog';
import { Info } from '@phosphor-icons/react';

interface SingleChoiceOptionsProps {
  options: IOption[];
  selectedOption: string;
  onChange: (selected: string) => void;
  setHasSafetyConcern?: (hasSafetyConcern: boolean) => void;
  expandableTextareaContent?: string;
  setExpandableTextareaContent?: (textareaContent: string) => void;
  expandableTextareaErrorMessage?: Record<string, string>;
  answerDisplayType?: number | string;
}

const SingleChoiceOptions = ({
  options,
  selectedOption,
  onChange,
  setHasSafetyConcern,
  expandableTextareaContent,
  setExpandableTextareaContent,
  expandableTextareaErrorMessage,
  answerDisplayType,
}: SingleChoiceOptionsProps) => {
  const [modalState, setModalState] = useState<{
    option: IOption | null;
    type: 'moreInfo' | 'safetyConcern' | null;
  }>({ option: null, type: null });

  const openModal = useCallback((option: IOption, type: 'moreInfo' | 'safetyConcern') => {
    setModalState({ option, type });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({ option: null, type: null });
  }, []);

  const handleOptionChange = useCallback(
    (selectedId: string) => {
      onChange(selectedId);
      const selectedOptionData = options.find((opt) => opt.id.toString() === selectedId);
      const hasSafetyConcern = selectedOptionData?.end_progress;
      if (hasSafetyConcern) {
        setHasSafetyConcern?.(true);
        openModal(selectedOptionData, 'safetyConcern');
      } else {
        setHasSafetyConcern?.(false);
      }
    },
    [onChange, options, setHasSafetyConcern, openModal],
  );

  const handleTextareaContentChange = (content: string) => {
    setExpandableTextareaContent?.(content);
  };

  const { option: selectedOptionData, type: modalType } = modalState;
  const isSafetyConcern = modalType === 'safetyConcern';

  return (
    <div className="flex flex-col w-full space-y-8 tablet:space-y-12">
      {options.map((option) => {
        const isSelected = selectedOption === option.id.toString();

        return (
          <Fragment key={option.id}>
            <button
              type="button"
              onClick={() => handleOptionChange(option.id.toString())}
              className={clsx(
                'w-full bg-paper relative text-ashgray text-body min-h-[54px] tablet:min-h-[74px] flex items-center text-start justify-between rounded-[8px] tablet:rounded-[12px] border transition-all',
                isSelected
                  ? `font-bold border-amberblack ${option.need_more_info ? 'mb-[6px]' : ''}`
                  : 'border border-off-white',
              )}
            >
              <div className="flex flex-col w-full">
                <div
                  className={`flex items-center p-16 tablet:p-[24px] ${option.helper_text ? 'pb-12 tablet:pb-20' : ''}`}
                >
                  {[1, 'image'].includes(answerDisplayType || '') && (
                    <div
                      className={`w-[106px] tablet:w-[153px] h-[113px] tablet:h-[143px] flex-shrink-0 bg-off-gray-soft rounded-tl-[8px] tablet:rounded-tl-[12px] ${option.helper_text ? '-ml-16 tablet:-ml-24 -mt-16 tablet:-mt-24 -mb-12 tablet:-mb-20' : '-m-16 tablet:-m-24 rounded-bl-[8px] tablet:rounded-bl-[12px]'} mr-16 tablet:mr-24`}
                    >
                      <img src={option.attachment} alt="Head image" className="w-full h-full" />
                    </div>
                  )}
                  <span>{option.answer_text}</span>
                </div>
                {option.helper_text && (
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openModal(option, 'moreInfo');
                    }}
                    className="text-body-sm tablet:text-body-xs font-normal bg-off-gray px-16 tablet:px-24 pt-[10px] tablet:pt-12 pb-12 tablet:pb-[14px] cursor-pointer rounded-b-[8px] tablet:rounded-b-[12px]"
                  >
                    <span className="text-ashgray flex items-center -ml-[3.23px] gap-[4.77px] tablet:gap-[6.385px]">
                      <Info size={17.24} /> See more info
                    </span>
                  </div>
                )}
              </div>
            </button>
            <ExpandableTextarea
              textareaContent={expandableTextareaContent || ''}
              setTextareaContent={handleTextareaContentChange}
              isVisible={isSelected && option.need_more_info}
              questionTitle={option.user_text_title || ''}
              textAreaPlaceHolder={option.user_text_placeholder}
            />
            {expandableTextareaErrorMessage &&
              expandableTextareaErrorMessage[option.id] &&
              option.need_more_info &&
              isSelected &&
              !expandableTextareaContent && (
                <p role="alert" className="text-xs text-[var(--color-error)] ml-[4px]">
                  {expandableTextareaErrorMessage[option.id]}
                </p>
              )}
          </Fragment>
        );
      })}
      {selectedOptionData && modalType && (
        <HelperDialog
          isOpen
          isSafetyConcern={isSafetyConcern}
          title={selectedOptionData?.answer_text}
          description={selectedOptionData?.helper_text}
          buttonText="Return to Question"
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default SingleChoiceOptions;
